// @ts-nocheck

import { capitalize, chain, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { Layer, Source, useMap } from 'react-map-gl/mapbox';
import { useSelector } from 'react-redux';
import { normalize } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { mapPrefs } from 'sonata-static-storage/lib/samples/mapPreferences.samples';

import { getEventsByGeoH, getIndividualEvents } from '@/store/selectors/reporting.selectors';

function mapProp(property, defaultValue = 0) {
  return ['case', ['has', property], ['get', property], defaultValue];
}

function EventsLayer({
  footfallAttributionControlState,
  geoEventColor,
  individualEventsColor,
  mapId,
  metrics,
  selectedGeoEvent,
  show
}) {
  const { current: mapRef } = useMap();
  const map = mapRef.getMap();

  const formatEventsByGeoH = (formatEventsByGeoHInput = {}) => {
    return !footfallAttributionControlState || footfallAttributionControlState.impressions
      ? formatEventsByGeoHInput
      : {};
  };

  const formatIndividualEvents = (individualEventsInput = {}) => {
    if (footfallAttributionControlState) {
      const { features = [], ...rest } = individualEventsInput;
      return footfallAttributionControlState.storeVisits
        ? {
            ...rest,
            features: features.filter(
              feature =>
                feature.properties.type === 'footfall_attribution' || feature.properties.type === 'fa_venue_uplift'
            )
          }
        : {};
    }
    return individualEventsInput;
  };

  const eventsByGeoH = formatEventsByGeoH(useSelector(store => getEventsByGeoH(store, mapId)));
  const individualEvents = formatIndividualEvents(useSelector(store => getIndividualEvents(store, mapId)));

  const getEventOpacity = useCallback(
    event => {
      const maxEvents = chain(eventsByGeoH.features)
        .map(geoH => geoH.properties[event])
        .max()
        .value();
      return [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        0.8,
        ['/', ['*', mapProp(event), 0.8], maxEvents]
      ];
    },
    [eventsByGeoH]
  );

  const renderPopups = () => {
    let hoveredStateId = null;
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: '50px',
      offset: 10
    });
    map.on('mousemove', e => {
      popup.setLngLat(e.lngLat);
    });

    ['impressions', 'playouts'].forEach(layerByGeoEvent => {
      map.on('mousemove', `eventsByGeoH_${layerByGeoEvent}`, e => {
        if (e.features.length > 0) {
          if (hoveredStateId) {
            map.setFeatureState(
              {
                id: hoveredStateId,
                source: 'eventsByGeoH'
              },
              { hover: false }
            );
          }
          hoveredStateId = e.features[0].id;
          map.setFeatureState(
            {
              id: hoveredStateId,
              source: 'eventsByGeoH'
            },
            { hover: true }
          );
          map.getCanvas().style.cursor = 'pointer';
          const description = `${capitalize(layerByGeoEvent)}: ${normalize(e.features[0].properties[layerByGeoEvent], 0)}`;
          popup.setHTML(description).addTo(map);
        }
      });

      map.on('mouseleave', `eventsByGeoH_${layerByGeoEvent}`, () => {
        if (hoveredStateId) {
          map.setFeatureState(
            {
              id: hoveredStateId,
              source: 'eventsByGeoH'
            },
            { hover: false }
          );
        }
        hoveredStateId = null;
        map.getCanvas().style.cursor = '';
        popup.remove();
      });
    });
  };

  useEffect(() => {
    if (eventsMetrics.length) {
      renderPopups();
    }
  }, [metrics]);

  const { eventsMetrics, individualMetrics } = (metrics || []).reduce(
    (acc, actualMetric) => {
      if (['impressions', 'playouts'].includes(actualMetric)) {
        return {
          ...acc,
          eventsMetrics: [...acc.eventsMetrics, actualMetric]
        };
      }
      return {
        ...acc,
        individualMetrics: [...acc.individualMetrics, actualMetric]
      };
    },
    {
      eventsMetrics: [],
      individualMetrics: []
    }
  );

  return (
    <>
      {show && !isEmpty(eventsByGeoH) ? (
        <Source data={eventsByGeoH} id="eventsByGeoH" key="eventsByGeoH" type="geojson">
          {eventsMetrics.map(metric => {
            if (selectedGeoEvent && metric === selectedGeoEvent) {
              return (
                <Layer
                  id={`eventsByGeoH_${metric}`}
                  key={`eventsByGeoH_${metric}`}
                  paint={{
                    'fill-color': geoEventColor ?? mapPrefs.events.colors[metric],
                    'fill-opacity': getEventOpacity(metric),
                    'fill-outline-color': geoEventColor ?? mapPrefs.events.colors[metric]
                  }}
                  source="eventsByGeoH"
                  type="fill"
                />
              );
            }
            return null;
          })}
        </Source>
      ) : null}
      {show && !isEmpty(individualEvents) ? (
        <Source data={individualEvents} id="individualEvents" key="individualEvents" type="geojson">
          {individualMetrics.map(metric => {
            return (
              <Layer
                filter={['==', 'type', metric]}
                id={`individualEvents_${metric}`}
                key={`individualEvents_${metric}`}
                paint={{
                  'circle-color': individualEventsColor ?? mapPrefs.events.colors[metric],
                  'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 12, 6, 14, 10, 16, 14]
                }}
                source="individualEvents"
                type="circle"
              />
            );
          })}
        </Source>
      ) : null}
    </>
  );
}

EventsLayer.propTypes = {
  footfallAttributionControlState: PropTypes.object,
  geoEventColor: PropTypes.string,
  individualEventsColor: PropTypes.string,
  mapId: PropTypes.string.isRequired,
  metrics: PropTypes.array.isRequired,
  selectedGeoEvent: PropTypes.string,
  show: PropTypes.bool
};
EventsLayer.defaultProps = {
  show: true
};

export default EventsLayer;
